import type {
  DiagnosticsPayload,
  ModelState,
  ScreenshotState,
} from '@shared/support-bundles/contract'
import type { SerializedModel } from './capture/model'

export interface AssembleInput {
  message: string
  platform: string
  includeScreenshot: boolean
  includeModel: boolean
}

export interface AssembleDeps {
  collect: () => DiagnosticsPayload
  getModel: () => SerializedModel | null
  peekScreenshot: () => Uint8Array | undefined
  gzip: (value: unknown) => Promise<Uint8Array>
  redact: (value: unknown) => unknown
}

export interface AssembledBundle {
  message: string
  platform: string
  diagnostics: Uint8Array
  screenshot?: Uint8Array | undefined
  model?: Uint8Array | undefined
  screenshotState: ScreenshotState
  modelState: ModelState
}

/**
 * Collects, caps, redacts, serialises and compresses a bundle.
 *
 * The order is load-bearing and is the reason redaction takes a function rather
 * than happening inside `gzip`: compressing first would make the redaction a
 * no-op on compressed bytes, while every unit test asserting against the
 * intermediate object would still pass.
 *
 * The opt-out flags the receiver stores as columns are *derived* from the
 * detailed states rather than tracked alongside them, so the coarse and fine
 * records cannot drift into disagreeing about the same bundle.
 */
export async function assembleBundle(
  input: AssembleInput,
  deps: AssembleDeps,
): Promise<AssembledBundle> {
  const screenshotBytes = input.includeScreenshot ? deps.peekScreenshot() : undefined
  const screenshotState: ScreenshotState = !input.includeScreenshot
    ? 'opted_out'
    : screenshotBytes
      ? 'included'
      : 'failed'

  const model = input.includeModel ? deps.getModel() : null
  const modelState: ModelState = !input.includeModel
    ? 'opted_out'
    : model
      ? 'included'
      : 'none_open'

  // The states are not repeated inside the payload. They cross the wire as
  // their own text parts, which is where the receiver can filter on them; a
  // second copy in an opaque compressed blob would only be something to drift.
  const diagnostics = await deps.gzip(deps.redact(deps.collect()))
  const modelBytes = model ? await deps.gzip(deps.redact(model)) : undefined

  // Scrubbed separately, because it is a text part that the recursion over the
  // diagnostics object never touches — and it is the one field a human types
  // into, where the realistic leak is pasting an excerpt from a log.
  const message = deps.redact(input.message)

  return {
    message: typeof message === 'string' ? message : input.message,
    platform: input.platform,
    diagnostics,
    screenshot: screenshotBytes,
    model: modelBytes,
    screenshotState,
    modelState,
  }
}
